import camelCase from 'camelcase';
import PropTypes from 'prop-types';
import {
	useEffect, useState, useRef,
} from 'react';
import { useRouter } from 'next/router';
import Ad from '../../components/Ad/Ad';
import { AdSizes } from '../../components/Ad/AdSizes';
import AdobeAnalyticsDataLayer from '../../components/AdobeAnalytics/AdobeAnalyticsDataLayer';
import PostsBlockGridWithQuery from '../../components/PostBlock/PostBlockGridWithQuery';
import RecipeFilters from '../../components/RecipeFilter/RecipeFilters';
import { RECIPE_FILTER_QUERY } from '../../components/RecipeFilter/RecipeFilterQuery';
import { RECIPE_FILTERS_DATA } from '../../components/RecipeFilter/RecipeFiltersData';
import { TAG_PAGES_QUERY } from '../../components/Tags/TagPagesQuery';
import SeoYoast from '../../components/Seo/SeoYoast';
import SeoJsonLd, { parseForSeo } from '../../components/Seo/Json/SeoJsonLd';
import { initializeApollo } from '../../utils/apolloClient';
import SponsorAd from '../../components/Ad/SponsorAd';
import { fetchSeoMeta } from '../../utils/fetchSeoMeta';
import { isUsTraffic } from '../../utils/geoBlock';
import { handleSearchData } from '../../utils/analytics';

export default function Recipes({
	filterItems = '',
	initialOptions = { postTypes: 'ce_recipe', page: 1, limit: Recipes.DEFAULT_LIMIT },
	initialFilters = [],
	initialPosts = [],
	initialFoundPosts = 0,
	pageSeo,
}) {
	const [options, setOptions] = useState(initialOptions);
	const [filters, setFilters] = useState(initialFilters);
	const [initialLoad, setInitialLoad] = useState(true);
	const isFirstUpdate = useRef(true);
	const router = useRouter();

	useEffect(() => {
		if (!initialLoad) {
			handleSearchData();
		} else {
			setInitialLoad(false);
		}
	}, [initialLoad]);

	useEffect(() => {
		if (isFirstUpdate.current === true) return;
		// Translate the filters array into options for apollo query
		const newOptions = { ...options, page: 1 };
		// Add new filters to options
		filters.forEach((filter) => {
			const filterName = camelCase(filter.__typename);
			if (newOptions[filterName] === undefined) {
				newOptions[filterName] = filter.slug;
			} else {
				const newFilters = newOptions[filterName].split(',');
				if (!newFilters.some((item) => item === filter.slug)) {
					newFilters.push(filter.slug);
				}
				newOptions[filterName] = newFilters.join(',');
			}
		});
		// Remove unwanted filters from options
		Object.keys(RECIPE_FILTERS_DATA).forEach((key) => {
			const filterName = camelCase(RECIPE_FILTERS_DATA[key].title);
			if (newOptions[filterName]) {
				const oldFilters = newOptions[filterName].split(',');
				const newFilters = oldFilters.filter((item) => (filters.some((current) => (item === current.slug))));
				newOptions[filterName] = newFilters.join(',');
			} else {
				newOptions[filterName] = '';
			}
		});
		setOptions(newOptions);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters]);

	useEffect(() => {
		if (isFirstUpdate.current) {
			isFirstUpdate.current = false;
		}
	}, []);

	useEffect(() => {
		if (Object.keys(router.query).length === 0 && filters.length > 0) {
			setFilters([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.asPath]);

	const noResultsMessage = (
		<p className="no-results">
			Uh oh! We didn’t find any matches. Try removing filters to get more results.
		</p>
	);

	const seo = parseForSeo(initialPosts);
	const description = 'List of Recipes from Flavour Network';
	const page = 'Recipes - Flavour Network';

	return (
		<>
			<SeoYoast seo={pageSeo?.seo} />
			<SeoJsonLd seo={seo} description={description} pagename={page} />
			<AdobeAnalyticsDataLayer
				viewType="archive"
			/>
			<div className="recipes">
				<Ad
					sizes={{
						'xs sm md': AdSizes.HIDDEN,
						lg: AdSizes.LEADERBOARD,
						'xl xxl': AdSizes.LEADERBOARD_COMBO,
					}}
					htmlId="recipes-leaderboard-ad"
					wrapperClass="recipes-leaderboard-ad leaderboard-ad hide-mobile hide-tablet"
				/>
				<SponsorAd />
				<div className="recipes-header">
					<h1 className="recipes-title">Recipes</h1>
					<RecipeFilters
						filters={filters}
						setFilters={setFilters}
						filterItems={filterItems}
						filtersData={RECIPE_FILTERS_DATA}
					/>
				</div>
				<PostsBlockGridWithQuery
					initialPosts={initialPosts}
					initialFoundPosts={initialFoundPosts}
					options={options}
					setOptions={setOptions}
					noResultsMessage={noResultsMessage}
					className="recipes-block"
					passQueryVar={false}
				/>
			</div>
		</>
	);
}

Recipes.DEFAULT_LIMIT = 12;

export async function getServerSideProps(context) {
	const apolloClient = initializeApollo();
	const { req: { url } } = context;
	const excludeUS = isUsTraffic(context);

	const queryParams = new URLSearchParams(url.split('?')[1]);

	// Fetch Page Yoast SEO Meta
	const pageSeo = await fetchSeoMeta('/recipe');

	const response = await apolloClient.query({
		query: RECIPE_FILTER_QUERY,
	});

	if (response) {
		const { data: filterItems } = response;
		const initialFilters = [];
		const initialFilterStrings = {};
		// eslint-disable-next-line no-restricted-syntax
		for (const value of queryParams) {
			// Add param to initialFilters
			Object.keys(RECIPE_FILTERS_DATA).forEach((taxonomy) => {
				const foundItem = filterItems[taxonomy].nodes.find((item) => item.slug === value[1]);
				if (foundItem) {
					initialFilters.push(foundItem);
				}
			});
		}

		// Add param to initialOptions
		Object.keys(RECIPE_FILTERS_DATA).forEach((filterKey) => {
			const filterName = camelCase(RECIPE_FILTERS_DATA[filterKey].title);
			const filterArray = queryParams.getAll(filterName);
			initialFilterStrings[filterName] = filterArray.join(',');
		});

		const initialPage = parseInt(queryParams.get('page'), 10) || 1;

		const initialOptions = {
			page: initialPage,
			postTypes: 'ce_recipe',
			limit: Recipes.DEFAULT_LIMIT,
			...initialFilterStrings,
			excludeUS,
		};

		const variables = {
			...initialOptions,
			paged: 1,
			limit: (initialPage * Recipes.DEFAULT_LIMIT),
		};

		const responseData = await apolloClient.query({
			query: TAG_PAGES_QUERY,
			variables,
		});

		let initialPosts = [];
		let initialFoundPosts = 0;

		if (responseData) {
			const { data } = responseData;
			initialPosts = (data && data?.tagPages.found_posts) ? data?.tagPages?.posts : [];
			initialFoundPosts = data?.tagPages.found_posts;

			return {
				props: {
					filterItems,
					initialOptions,
					initialFilters,
					initialPosts,
					initialFoundPosts,
					pageSeo,
				},
			};
		}
	}

	return { notFound: true };
}

Recipes.propTypes = {
	filterItems: PropTypes.object,
	initialOptions: PropTypes.object,
	initialFilters: PropTypes.array,
	initialFoundPosts: PropTypes.number,
	initialPosts: PropTypes.array,
	pageSeo: PropTypes.object.isRequired,
};
